import { useCallback, useState } from 'react';
import type { ReviewDepth, Severity } from '../types/api';
import { SEVERITIES } from '../types/api';

export interface Preferences {
  defaultDepth: ReviewDepth;
  defaultSeverities: Severity[];
}

const KEY = 'prs-preferences';
export const DEFAULT_PREFERENCES: Preferences = { defaultDepth: 'quick', defaultSeverities: [...SEVERITIES] };

export function readPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      defaultDepth: parsed.defaultDepth === 'thorough' ? 'thorough' : 'quick',
      defaultSeverities: Array.isArray(parsed.defaultSeverities)
        ? parsed.defaultSeverities.filter((s): s is Severity => (SEVERITIES as readonly string[]).includes(s))
        : DEFAULT_PREFERENCES.defaultSeverities,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/** Per-browser review preferences. Not secrets, so localStorage is appropriate. */
export function usePreferences() {
  const [prefs, setPrefs] = useState<Preferences>(readPreferences);
  const save = useCallback((next: Preferences) => {
    setPrefs(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
  }, []);
  return { prefs, save };
}
